import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "submit", "timezoneField"]

  connect() {
    this.submitting = false
    this.pendingContent = null

    if (this.hasInputTarget) {
      this.inputTarget.focus()
    }
    this.updateSubmitButton()
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.submit()
    }
  }

  submit() {
    if (!this.hasInputTarget) return
    if (this.submitting) return

    const content = this.inputTarget.value.trim()
    if (!content) return

    // Save content for recovery on failure
    this.pendingContent = content
    this.submitting = true
    this.setSubmitLoading(true)

    // Piggyback browser timezone on every message so the server always has it
    if (this.hasTimezoneFieldTarget) {
      try {
        this.timezoneFieldTarget.value = Intl.DateTimeFormat().resolvedOptions().timeZone || ""
      } catch (_) { /* timezone is best-effort */ }
    }

    this.element.requestSubmit()
  }

  reset(event) {
    const success = event?.detail?.success !== false

    if (success) {
      if (this.hasInputTarget) {
        this.inputTarget.value = ""
        this.inputTarget.style.height = "auto"
        this.inputTarget.focus()
      }
      this.pendingContent = null
    } else {
      // On failure, restore content if the input was somehow cleared
      if (this.hasInputTarget && this.pendingContent && !this.inputTarget.value.trim()) {
        this.inputTarget.value = this.pendingContent
      }
      this.inputTarget?.focus()
    }

    this.submitting = false
    this.setSubmitLoading(false)
    this.updateSubmitButton()
  }

  setSubmitLoading(loading) {
    if (!this.hasSubmitTarget) return
    if (loading) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add('opacity-50', 'cursor-not-allowed')
      this.submitTarget.classList.remove('cursor-pointer', 'hover:bg-[#5f39d6]')
    }
  }

  autoResize() {
    if (this.hasInputTarget) {
      const textarea = this.inputTarget
      textarea.style.height = "auto"
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px"
    }
    this.updateSubmitButton()
  }

  updateSubmitButton() {
    if (!this.hasSubmitTarget || !this.hasInputTarget) return
    if (this.submitting) return

    const content = this.inputTarget.value.trim()
    const isEmpty = !content || content.length === 0

    if (isEmpty) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add('opacity-50', 'cursor-not-allowed')
      this.submitTarget.classList.remove('cursor-pointer', 'hover:bg-[#5f39d6]')
    } else {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      this.submitTarget.classList.add('cursor-pointer', 'hover:bg-[#5f39d6]')
    }
  }

  uploadImage(event) {
    event.preventDefault()
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (e) => {
      const file = e.target.files[0]
      if (file) {
        this.submitImage(file)
      }
    }
    input.click()
  }

  async submitImage(file) {
    const conversationId = this.element.action.match(/conversations\/([^\/]+)/)[1]
    const formData = new FormData()
    formData.append('image', file)

    try {
      const response = await fetch(`/conversations/${conversationId}/image_uploads`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: formData
      })

      if (response.ok) {
        const turboStream = await response.text()
        Turbo.renderStreamMessage(turboStream)
      } else {
        const error = await response.json()
        console.error('Image upload failed:', error)
        alert(`Image upload failed: ${error.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Image upload error:', error)
      alert('Image upload failed. Please try again.')
    }
  }

  uploadFile(event) {
    event.preventDefault()
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.pdf,.doc,.docx,.txt,.csv,.xlsx,.pptx'
    input.onchange = (e) => {
      const file = e.target.files[0]
      if (file) {
        this.submitFile(file)
      }
    }
    input.click()
  }

  async submitFile(file) {
    const conversationId = this.element.action.match(/conversations\/([^\/]+)/)[1]
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await fetch(`/conversations/${conversationId}/file_uploads`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: formData
      })

      if (response.ok) {
        const turboStream = await response.text()
        Turbo.renderStreamMessage(turboStream)
      } else {
        const error = await response.json()
        console.error('File upload failed:', error)
        alert(`File upload failed: ${error.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('File upload error:', error)
      alert('File upload failed. Please try again.')
    }
  }
}
